import { useState, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Copy, Save, Book, Dice1, Swords, Sparkles, Users, Package, TrendingUp, FileText, Plus, Edit, RefreshCw, MessageSquare, Upload, AlertCircle, Shield } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import type { SystemAnalysisResult } from '@creanizer/types'
import { useScenarioStore } from '../../../../stores/scenarioStore'
import { useGenerator } from '../../../../hooks/useGenerator'
import { readMultipleFiles, FILE_ACCEPT } from '../../utils/fileReader'
// Toast notifications removed

interface SystemAnalysisResultProps {
  result: SystemAnalysisResult
  originalInput: {
    systemName?: string
    systemVersion?: string
    sourceType?: string
    sourceText?: string
  }
  onResultUpdate?: (result: SystemAnalysisResult) => void
}

export function SystemAnalysisResult({ result, originalInput, onResultUpdate }: SystemAnalysisResultProps) {
  const { addSystem } = useScenarioStore()
  const [additionalSource, setAdditionalSource] = useState('')
  const [correctionInstruction, setCorrectionInstruction] = useState('')
  const [isRefining, setIsRefining] = useState(false)
  const [activeTab, setActiveTab] = useState('view')
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([])
  const [additionalSourceType, setAdditionalSourceType] = useState<'rulebook' | 'play_log' | 'description' | 'mixed'>('mixed')
  const [currentResult, setCurrentResult] = useState(result)
  const [isSaved, setIsSaved] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const generator = useGenerator('scenario')
  
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    console.log(`${label}をコピーしました`)
  }
  
  const handleSave = () => {
    addSystem({
      name: originalInput.systemName || currentResult.systemInfo?.name || '無題のシステム',
      input: {
        mode: 'analyze_system',
        systemAnalysisSettings: {
          sourceType: (originalInput.sourceType as 'rulebook' | 'play_log' | 'description' | 'mixed') || 'description',
          sourceText: originalInput.sourceText || '',
          systemName: originalInput.systemName,
          systemVersion: originalInput.systemVersion,
          includeExamples: true
        }
      },
      result: currentResult,
      category: currentResult.systemInfo?.category || 'trpg',
      tags: []
    })
    setIsSaved(true)
    console.log('システムを保存しました')
  }
  
  const formatSystemData = () => {
    return JSON.stringify(currentResult, null, 2)
  }
  
  // ファイルアップロード処理
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    
    setIsProcessingFiles(true)
    setUploadedFiles([])
    
    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)
      
      if (errors.length > 0) {
        console.error('ファイル読み込みエラー:', errors)
      }
      
      if (combinedText) {
        setAdditionalSource(prev => {
          const newText = prev ? `${prev}\n\n${combinedText}` : combinedText
          return newText
        })
        setUploadedFiles(fileInfos.map(info => info.fileName))
      }
    } catch (error) {
      console.error('ファイル処理エラー:', error)
    } finally {
      setIsProcessingFiles(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }
  
  // 追加解析処理
  const handleAdditionalAnalysis = async () => {
    if (!additionalSource.trim()) return
    
    setIsRefining(true)
    try {
      const refinedInput = {
        mode: 'analyze_system' as const,
        systemAnalysisSettings: {
          sourceType: additionalSourceType,
          sourceText: `【既存の解析結果】\n${JSON.stringify(currentResult, null, 2)}\n\n【追加ソース（タイプ: ${additionalSourceType}）】\n${additionalSource}`,
          systemName: originalInput.systemName,
          systemVersion: originalInput.systemVersion,
          includeExamples: true,
          extractionFocus: ['既存の解析結果をベースに追加情報を統合']
        }
      }
      
      const response = await generator.mutateAsync(refinedInput)
      const newResult = response.result || response
      
      if (newResult) {
        setCurrentResult(newResult as SystemAnalysisResult)
        if (onResultUpdate) {
          onResultUpdate(newResult as SystemAnalysisResult)
        }
        setIsSaved(false) // 新しい結果があるので保存状態をリセット
        console.log('追加解析を実行し、結果を更新しました')
        setAdditionalSource('')
        setUploadedFiles([])
        setActiveTab('view') // 結果表示タブに切り替え
      }
    } catch (error) {
      console.error('追加解析エラー:', error)
    } finally {
      setIsRefining(false)
    }
  }
  
  // 修正指示処理
  const handleCorrection = async () => {
    if (!correctionInstruction.trim()) return
    
    setIsRefining(true)
    try {
      const correctionInput = {
        mode: 'analyze_system' as const,
        systemAnalysisSettings: {
          sourceType: 'mixed' as const,
          sourceText: `【現在の解析結果】\n${JSON.stringify(currentResult, null, 2)}\n\n【修正指示】\n${correctionInstruction}\n\n上記の修正指示に従って、解析結果を修正してください。`,
          systemName: originalInput.systemName,
          systemVersion: originalInput.systemVersion,
          includeExamples: true
        }
      }
      
      const response = await generator.mutateAsync(correctionInput)
      const newResult = response.result || response
      
      if (newResult) {
        setCurrentResult(newResult as SystemAnalysisResult)
        if (onResultUpdate) {
          onResultUpdate(newResult as SystemAnalysisResult)
        }
        setIsSaved(false) // 新しい結果があるので保存状態をリセット
        console.log('修正指示を実行し、結果を更新しました')
        setCorrectionInstruction('')
        setActiveTab('view') // 結果表示タブに切り替え
      }
    } catch (error) {
      console.error('修正処理エラー:', error)
    } finally {
      setIsRefining(false)
    }
  }
  
  return (
    <div className="space-y-6">
      {/* タブナビゲーション */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="view">
            <Book className="w-4 h-4 mr-2" />
            結果表示
          </TabsTrigger>
          <TabsTrigger value="refine">
            <RefreshCw className="w-4 h-4 mr-2" />
            追加解析
          </TabsTrigger>
          <TabsTrigger value="correct">
            <Edit className="w-4 h-4 mr-2" />
            修正指示
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="view" className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Book className="w-5 h-5" />
                {currentResult.systemInfo?.name || originalInput.systemName || '解析済みシステム'}
              </CardTitle>
              {(result.systemInfo?.version || originalInput.systemVersion) && (
                <CardDescription>
                  バージョン: {currentResult.systemInfo?.version || originalInput.systemVersion}
                </CardDescription>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(formatSystemData(), 'システムデータ')}
              >
                <Copy className="w-4 h-4 mr-2" />
                コピー
              </Button>
              <Button
                size="sm"
                variant={isSaved ? 'secondary' : 'default'}
                onClick={handleSave}
                disabled={isSaved}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaved ? '保存済み' : '保存'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {currentResult.systemInfo?.description && (
            <div className="text-gray-600 dark:text-gray-400">
              {currentResult.systemInfo?.description}
            </div>
          )}
        </CardContent>
      </Card>
      
      {currentResult.coreRules && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              コアルール
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.coreRules.resolution && (
              <div>
                <h4 className="font-semibold mb-2">判定方法</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {currentResult.coreRules.resolution}
                </div>
              </div>
            )}
            {currentResult.coreRules.rollProcedure && (
              <div>
                <h4 className="font-semibold mb-2">判定手順</h4>
                <ol className="list-decimal list-inside space-y-1">
                  {Object.entries(currentResult.coreRules.rollProcedure)
                    .filter(([key, value]) => value)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([key, value]) => (
                      <li key={key} className="text-sm text-gray-600 dark:text-gray-400">
                        {value}
                      </li>
                    ))}
                </ol>
              </div>
            )}
            {currentResult.coreRules.difficultyLevels && currentResult.coreRules.difficultyLevels.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">難易度レベル</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1">レベル</th>
                      <th className="text-left py-1">目標値</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentResult.coreRules.difficultyLevels.map((level, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-1 text-gray-600 dark:text-gray-400">
                          {level['レベル'] || level.level}
                        </td>
                        <td className="py-1 text-gray-600 dark:text-gray-400">
                          {level['目標値'] || level.target}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {currentResult.coreRules.successDegrees && currentResult.coreRules.successDegrees.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">成功度</h4>
                <div className="space-y-2">
                  {currentResult.coreRules.successDegrees.map((degree, index) => (
                    <div key={index} className="border-l-4 border-blue-200 pl-3">
                      <div className="font-medium text-sm">
                        {degree['名称'] || degree.name}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        条件: {degree['条件'] || degree.condition}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        効果: {degree['効果'] || degree.effect}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {currentResult.coreRules.criticalSuccess && (
              <div>
                <h4 className="font-semibold mb-2">クリティカル成功</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.coreRules.criticalSuccess}
                </div>
              </div>
            )}
            {currentResult.coreRules.criticalFailure && (
              <div>
                <h4 className="font-semibold mb-2">クリティカル失敗</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.coreRules.criticalFailure}
                </div>
              </div>
            )}
            {currentResult.coreRules.contestedRolls && (
              <div>
                <h4 className="font-semibold mb-2">対抗判定</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.coreRules.contestedRolls}
                </div>
              </div>
            )}
            {currentResult.coreRules.extendedTests && (
              <div>
                <h4 className="font-semibold mb-2">連続判定</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.coreRules.extendedTests}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.diceSystem && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Dice1 className="w-5 h-5" />
              ダイスシステム
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">
                基本: {currentResult.diceSystem.base}
              </Badge>
              {currentResult.diceSystem.modifiers && result.diceSystem.modifiers.map((mod, index) => (
                <Badge key={index} variant="outline">
                  {mod}
                </Badge>
              ))}
            </div>
            {currentResult.diceSystem.special && result.diceSystem.special.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">特殊ルール</h4>
                <ul className="list-disc list-inside space-y-1">
                  {currentResult.diceSystem.special.map((rule, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.characterCreation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              キャラクター作成
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.characterCreation.attributes && (
              <div>
                <h4 className="font-semibold mb-2">能力値</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.characterCreation.attributes.map((attr, index) => (
                    <Badge key={index}>
                      {attr}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {currentResult.characterCreation.races && (
              <div>
                <h4 className="font-semibold mb-2">種族</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.characterCreation.races.map((race, index) => (
                    <Badge key={index} variant="outline">
                      {race}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {currentResult.characterCreation.classes && (
              <div>
                <h4 className="font-semibold mb-2">クラス/職業</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.characterCreation.classes.map((cls, index) => (
                    <Badge key={index} variant="outline">
                      {cls}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {currentResult.characterCreation.skills && (
              <div>
                <h4 className="font-semibold mb-2">スキル/技能</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {currentResult.characterCreation.skills.slice(0, 12).map((skill, index) => (
                    <div key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      • {skill}
                    </div>
                  ))}
                  {currentResult.characterCreation.skills.length > 12 && (
                    <div className="text-sm text-gray-500">
                      他{currentResult.characterCreation.skills.length - 12}個
                    </div>
                  )}
                </div>
              </div>
            )}
            {currentResult.characterCreation.steps && currentResult.characterCreation.steps.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">作成手順</h4>
                <ol className="list-decimal list-inside space-y-1">
                  {currentResult.characterCreation.steps.map((step, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {currentResult.characterCreation.backgrounds && currentResult.characterCreation.backgrounds.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">背景/出自</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.characterCreation.backgrounds.map((bg, index) => (
                    <Badge key={index} variant="secondary">
                      {bg}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {currentResult.characterCreation.pointBuy && (
              <div>
                <h4 className="font-semibold mb-2">ポイントバイ</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.characterCreation.pointBuy}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.combatRules && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Swords className="w-5 h-5" />
              戦闘ルール
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.combatRules.overview && (
              <div>
                <h4 className="font-semibold mb-2">概要</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.combatRules.overview}
                </div>
              </div>
            )}
            {currentResult.combatRules.roundSequence && currentResult.combatRules.roundSequence.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">ラウンド進行</h4>
                <ol className="list-decimal list-inside space-y-1">
                  {currentResult.combatRules.roundSequence.map((step, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {currentResult.combatRules.initiative && (
              <div>
                <h4 className="font-semibold mb-2">イニシアチブ</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.combatRules.initiative}
                </div>
              </div>
            )}
            {currentResult.combatRules.actions && (
              <div>
                <h4 className="font-semibold mb-2">行動</h4>
                <ul className="list-disc list-inside space-y-1">
                  {currentResult.combatRules.actions.map((action, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {typeof action === 'string' 
                        ? action 
                        : action['行動'] 
                          ? `${action['行動']}: ${action['説明']}`
                          : action.action 
                            ? `${action.action}: ${action.description}`
                            : JSON.stringify(action)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {currentResult.combatRules.attackProcedure && (
              <div>
                <h4 className="font-semibold mb-2">攻撃手順</h4>
                <ol className="list-decimal list-inside space-y-1">
                  {Object.entries(currentResult.combatRules.attackProcedure)
                    .filter(([key, value]) => value)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([key, value]) => (
                      <li key={key} className="text-sm text-gray-600 dark:text-gray-400">
                        {value}
                      </li>
                    ))}
                </ol>
              </div>
            )}
            {currentResult.combatRules.damage && (
              <div>
                <h4 className="font-semibold mb-2">ダメージ処理</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.combatRules.damage}
                </div>
              </div>
            )}
            {currentResult.combatRules.healing && (
              <div>
                <h4 className="font-semibold mb-2">回復ルール</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.combatRules.healing}
                </div>
              </div>
            )}
            {currentResult.combatRules.death && (
              <div>
                <h4 className="font-semibold mb-2">死亡・気絶ルール</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.combatRules.death}
                </div>
              </div>
            )}
            {currentResult.combatRules.conditions && (
              <div>
                <h4 className="font-semibold mb-2">状態異常</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.combatRules.conditions.map((condition, index) => (
                    <Badge key={index} variant="secondary">
                      {condition}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.nonCombatRules && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Book className="w-5 h-5" />
              非戦闘系判定
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.nonCombatRules.skillChecks && (
              <div>
                <h4 className="font-semibold mb-2">技能判定</h4>
                <div className="space-y-2">
                  {Object.entries(result.nonCombatRules.skillChecks).map(([key, value]) => value && (
                    <div key={key} className="text-sm">
                      <span className="font-medium capitalize">{key}: </span>
                      <span className="text-gray-600 dark:text-gray-400">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {currentResult.nonCombatRules.challengeResolution && (
              <div>
                <h4 className="font-semibold mb-2">課題解決</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.nonCombatRules.challengeResolution}
                </div>
              </div>
            )}
            {currentResult.nonCombatRules.groupTests && (
              <div>
                <h4 className="font-semibold mb-2">集団判定</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.nonCombatRules.groupTests}
                </div>
              </div>
            )}
            {currentResult.nonCombatRules.assistedTests && (
              <div>
                <h4 className="font-semibold mb-2">協力判定</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.nonCombatRules.assistedTests}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.sessionStructure && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              セッション構造
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.sessionStructure.overview && (
              <div>
                <h4 className="font-semibold mb-2">概要</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.sessionStructure.overview}
                </div>
              </div>
            )}
            {currentResult.sessionStructure.sceneTypes && (
              <div>
                <h4 className="font-semibold mb-2">シーンタイプ</h4>
                <div className="space-y-2">
                  {currentResult.sessionStructure.sceneTypes.map((scene, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium">
                        {scene['タイプ'] || scene.type || 'タイプ不明'}: 
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {scene['説明'] || scene.description || ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {currentResult.sessionStructure.handoutTiming && (
              <div>
                <h4 className="font-semibold mb-2">ハンドアウト配布タイミング</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.sessionStructure.handoutTiming}
                </div>
              </div>
            )}
            {currentResult.sessionStructure.playerBriefing && (
              <div>
                <h4 className="font-semibold mb-2">プレイヤーへの情報提供</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.sessionStructure.playerBriefing}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.playerSupport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              プレイヤー支援ツール
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.playerSupport.handouts && (
              <div>
                <h4 className="font-semibold mb-2">ハンドアウト</h4>
                {currentResult.playerSupport.handouts.types && result.playerSupport.handouts.types.length > 0 && (
                  <div className="mb-2">
                    <p className="text-sm font-medium">種類:</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {currentResult.playerSupport.handouts.types.map((type, index) => (
                        <Badge key={index} variant="outline">{type}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {currentResult.playerSupport.handouts.format && (
                  <p className="text-sm">
                    <span className="font-medium">フォーマット: </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {currentResult.playerSupport.handouts.format}
                    </span>
                  </p>
                )}
                {currentResult.playerSupport.handouts.timing && (
                  <p className="text-sm">
                    <span className="font-medium">配布タイミング: </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      {currentResult.playerSupport.handouts.timing}
                    </span>
                  </p>
                )}
              </div>
            )}
            {currentResult.playerSupport.characterSheets && (
              <div>
                <h4 className="font-semibold mb-2">キャラクターシート</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.playerSupport.characterSheets}
                </div>
              </div>
            )}
            {currentResult.playerSupport.quickReference && (
              <div>
                <h4 className="font-semibold mb-2">クイックリファレンス</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.playerSupport.quickReference}
                </div>
              </div>
            )}
            {currentResult.playerSupport.playAids && (
              <div>
                <h4 className="font-semibold mb-2">プレイ補助ツール</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.playerSupport.playAids}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.magicSystem && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              魔法/特殊能力
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.magicSystem.types && (
              <div>
                <h4 className="font-semibold mb-2">魔法の種類</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.magicSystem.types.map((type, index) => (
                    <Badge key={index}>
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {currentResult.magicSystem.casting && (
              <div>
                <h4 className="font-semibold mb-2">詠唱/発動</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.magicSystem.casting}
                </div>
              </div>
            )}
            {currentResult.magicSystem.costs && (
              <div>
                <h4 className="font-semibold mb-2">コスト</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.magicSystem.costs}
                </div>
              </div>
            )}
            {currentResult.magicSystem.limitations && (
              <div>
                <h4 className="font-semibold mb-2">制限</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.magicSystem.limitations}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.advancementRules && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              成長/レベルアップ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.advancementRules.experience && (
              <div>
                <h4 className="font-semibold mb-2">経験値</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.advancementRules.experience}
                </div>
              </div>
            )}
            {currentResult.advancementRules.levels && (
              <div>
                <h4 className="font-semibold mb-2">レベル</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.advancementRules.levels}
                </div>
              </div>
            )}
            {currentResult.advancementRules.improvements && (
              <div>
                <h4 className="font-semibold mb-2">成長要素</h4>
                <ul className="list-disc list-inside space-y-1">
                  {currentResult.advancementRules.improvements.map((improvement, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {improvement}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.itemsEquipment && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              アイテム/装備
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.itemsEquipment.categories && (
              <div>
                <h4 className="font-semibold mb-2">カテゴリー</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.itemsEquipment.categories.map((category, index) => (
                    <Badge key={index} variant="outline">
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {currentResult.itemsEquipment.rarity && (
              <div>
                <h4 className="font-semibold mb-2">レアリティ</h4>
                <div className="flex flex-wrap gap-2">
                  {currentResult.itemsEquipment.rarity.map((rarity, index) => (
                    <Badge key={index} variant="secondary">
                      {rarity}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {currentResult.itemsEquipment.special && (
              <div>
                <h4 className="font-semibold mb-2">特殊アイテム</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.itemsEquipment.special}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.specialRules && currentResult.specialRules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              特殊ルール
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.specialRules.map((rule, index) => (
              <div key={index} className="border-l-4 border-yellow-200 pl-3">
                <h4 className="font-semibold mb-1">{rule.name}</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {rule.description}
                </div>
                {rule.examples && rule.examples.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">例:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {rule.examples.map((example, exIndex) => (
                        <li key={exIndex} className="text-xs text-gray-500">
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      
      {currentResult.terminology && currentResult.terminology.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Book className="w-5 h-5" />
              用語集
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {currentResult.terminology.slice(0, 20).map((term, index) => (
                <div key={index} className="border-b pb-2 last:border-0">
                  <dt className="font-medium text-sm mb-1">{term.term}</dt>
                  <dd className="text-sm text-gray-600 dark:text-gray-400 pl-4">
                    {term.definition}
                  </dd>
                </div>
              ))}
              {currentResult.terminology.length > 20 && (
                <p className="text-sm text-gray-500 italic">
                  他{currentResult.terminology.length - 20}個の用語があります
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {currentResult.gmGuidance && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              GM向けガイダンス
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentResult.gmGuidance.difficulty && (
              <div>
                <h4 className="font-semibold mb-2">難易度調整</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.gmGuidance.difficulty}
                </div>
              </div>
            )}
            {currentResult.gmGuidance.encounters && (
              <div>
                <h4 className="font-semibold mb-2">遭遇設計</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.gmGuidance.encounters}
                </div>
              </div>
            )}
            {currentResult.gmGuidance.rewards && (
              <div>
                <h4 className="font-semibold mb-2">報酬設定</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.gmGuidance.rewards}
                </div>
              </div>
            )}
            {currentResult.gmGuidance.pacing && (
              <div>
                <h4 className="font-semibold mb-2">ペース配分</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.gmGuidance.pacing}
                </div>
              </div>
            )}
            {currentResult.gmGuidance.rulings && (
              <div>
                <h4 className="font-semibold mb-2">裁定指針</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.gmGuidance.rulings}
                </div>
              </div>
            )}
            {currentResult.gmGuidance.improvisation && (
              <div>
                <h4 className="font-semibold mb-2">即興対応</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentResult.gmGuidance.improvisation}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {currentResult.examples && result.examples.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>実例</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentResult.examples.map((example, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                    {example}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>
        
        {/* 追加解析タブ */}
        <TabsContent value="refine" className="space-y-6">
          <Alert>
            <Plus className="h-4 w-4" />
            <AlertDescription>
              追加のソースを入力して、既存の解析結果をより詳細に補強できます。
              ルールブックの別の章や、プレイログ、FAQなどを追加してください。
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>追加ソース入力</CardTitle>
              <CardDescription>
                既存の解析結果に統合したい追加情報を入力してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="source-type">ソースの種類</Label>
                <Select value={additionalSourceType} onValueChange={(value: any) => setAdditionalSourceType(value)}>
                  <SelectTrigger id="source-type" className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rulebook">ルールブック</SelectItem>
                    <SelectItem value="play_log">プレイログ</SelectItem>
                    <SelectItem value="description">説明文書</SelectItem>
                    <SelectItem value="mixed">混合（複数種類）</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  追加するソースの種類を選択してください。精度向上のため適切なタイプを選ぶことを推奨します。
                </p>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="additional-source">追加テキスト</Label>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={FILE_ACCEPT}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isProcessingFiles}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {isProcessingFiles ? 'ファイル処理中...' : 'ファイルから読込'}
                    </Button>
                    {additionalSource && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setAdditionalSource('')
                          setUploadedFiles([])
                        }}
                      >
                        クリア
                      </Button>
                    )}
                  </div>
                </div>
                
                {uploadedFiles.length > 0 && (
                  <Alert className="mb-2">
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      読み込んだファイル: {uploadedFiles.join(', ')}
                    </AlertDescription>
                  </Alert>
                )}
                
                <Textarea
                  id="additional-source"
                  value={additionalSource}
                  onChange={(e) => setAdditionalSource(e.target.value)}
                  placeholder="例：戦闘ルールの詳細、魔法システムの追加情報、上級ルールなど（ファイルアップロードまたは直接入力）"
                  rows={10}
                  className="mt-2 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {additionalSource.length.toLocaleString()}文字
                </p>
              </div>
              
              <Button
                onClick={handleAdditionalAnalysis}
                disabled={!additionalSource.trim() || isRefining}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {isRefining ? '解析中...' : '追加解析を実行'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* 修正指示タブ */}
        <TabsContent value="correct" className="space-y-6">
          <Alert>
            <MessageSquare className="h-4 w-4" />
            <AlertDescription>
              解析結果の間違いや不足を指摘して、修正指示を出すことができます。
              具体的な修正内容を日本語で記述してください。
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>修正指示</CardTitle>
              <CardDescription>
                現在の解析結果に対する修正指示を入力してください
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="correction-instruction">修正内容</Label>
                <Textarea
                  id="correction-instruction"
                  value={correctionInstruction}
                  onChange={(e) => setCorrectionInstruction(e.target.value)}
                  placeholder="例：
・ダイスシステムは2d6ではなく3d6を使用します
・クリティカルは6のゾロ目ではなく、18の場合です
・魔法の消費MPが間違っています。正しくは...
・戦闘の行動順序は敏捷度ではなくイニシアチブ値で決まります"
                  rows={10}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {correctionInstruction.length.toLocaleString()}文字
                </p>
              </div>
              
              <Button
                onClick={handleCorrection}
                disabled={!correctionInstruction.trim() || isRefining}
                className="w-full"
              >
                <Edit className="w-4 h-4 mr-2" />
                {isRefining ? '修正中...' : '修正を適用'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}